import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { AppointmentCancelReason } from "@/server-types";
import { useCalendarStore } from "../../../store/useCalendarStore";
import { getAppointmentCancelReasonOptions } from "@/constants/cancelReasons";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@heroui/react";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { api } from "@/lib/api-client";
import { useForm } from "@mantine/form";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeCheckbox } from "@/Components/NextBase/LimeCheckbox";
import { AppointmentRefundFields } from "@/Components/AppointmentModal/components/AppointmentRefundFields";
import {
  SubscriptionCancellationDecisions,
  subscriptionDecisionDefaults,
  toSubscriptionCancellationDecision,
} from "@/Components/AppointmentModal/components/SubscriptionCancellationDecisions";
import { useClientFeature } from "@/hooks/useClientFeature";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { useEffect, useState } from "react";
import { toCents } from "@/utils";

type CancelModalProps = {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: number;
  userAppointmentId?: number;
  paymentIntentId?: string | null;
  isRepeating: boolean;
  isLinked: boolean;
};

export const AppointmentCancelModal = ({
  isOpen,
  onClose,
  appointmentId,
  userAppointmentId,
  paymentIntentId,
  isRepeating = false,
  isLinked = false,
}: CancelModalProps) => {
  const { hasCancelNotification, closeModals } = useCalendarStore(
    (store) => store,
  );

  const { mutateAsync: deleteAppointment } =
    api.appointment.useDeleteAppointment();

  const { isFeatureEnabled } = useClientFeature();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: payment,
    isPending: isLoadingPayment,
    processedErrorMessage: getPaymentErrorMessage,
  } = api.stripe.useGetPaymentForOverview(
    userAppointmentId,
    isOpen &&
      isFeatureEnabled("payments") &&
      userAppointmentId != null &&
      paymentIntentId != null,
  );

  const { data: cancelPreflight } =
    api.appointment.useGetAppointmentCancelPreflight({
      appointmentId,
      enabled: isOpen && isFeatureEnabled("customerSubscriptionMakeupCredits"),
    });

  const form = useForm<{
    shouldRefundPayment: boolean;
    refundAmount: number;
    refundReason: "requested_by_customer" | "duplicate" | "fraudulent";
    deleteAllRepeating: boolean;
    deleteAllLinked: boolean;
    sendCancelNotifications: boolean;
    cancelReason: AppointmentCancelReason;
    customCancelReason: string;
    excludeFromSubscriptionUsage: boolean;
    issueMakeupCredit: boolean;
    reissueCredit: boolean;
    extendCredit: boolean;
  }>({
    initialValues: {
      shouldRefundPayment: false,
      refundAmount: 0,
      refundReason: "requested_by_customer",
      deleteAllRepeating: false,
      deleteAllLinked: true,
      sendCancelNotifications: hasCancelNotification,
      cancelReason: "NoReasonProvided",
      customCancelReason: "",
      excludeFromSubscriptionUsage: false,
      issueMakeupCredit: false,
      reissueCredit: true,
      extendCredit: false,
    },
    validate: {
      customCancelReason: (value, values) => {
        if (values.cancelReason === "Custom" && value.trim().length === 0) {
          return t`Vnesite razlog za preklic`;
        }

        return null;
      },
      refundAmount: (value, values) => {
        if (!values.shouldRefundPayment) {
          return null;
        }

        if (value < 0.01) {
          return t`Znesek mora biti večji od 0`;
        }

        if (value > (payment?.refundableAmountCents || 0) / 100) {
          return t`Znesek ne sme biti večji od zneska plačila`;
        }

        return null;
      },
    },
  });

  useEffect(() => {
    if (!cancelPreflight) {
      return;
    }

    form.setValues(subscriptionDecisionDefaults(cancelPreflight));
  }, [cancelPreflight]);

  useEffect(() => {
    if (!payment) {
      return;
    }

    form.setValues({
      refundAmount: payment.maxNoLossRefundAmountCents / 100,
    });
  }, [payment]);

  const cancelReasonOptions = getAppointmentCancelReasonOptions();

  const handleConfirm = async (values: typeof form.values) => {
    setIsSubmitting(true);

    try {
      await deleteAppointment({
        appointmentId,
        body: {
          delete_repeating: values.deleteAllRepeating,
          delete_linked: values.deleteAllLinked,
          sendCancelNotifications: values.sendCancelNotifications,
          cancelReason: values.cancelReason,
          customCancelReason: values.customCancelReason,
          paymentRefundData: values.shouldRefundPayment
            ? {
                amountCents: toCents(values.refundAmount),
                reason: values.refundReason,
              }
            : undefined,
          subscriptionDecision: toSubscriptionCancellationDecision(
            cancelPreflight,
            values,
          ),
        },
      });
      closeModals();
      onClose();
    } catch (e) {
      console.error("Error deleting appointment:", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <ModalContent>
        {(onClose) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const isValid = form.validate();
              if (isValid) {
                const values = form.getValues();
                handleConfirm(values);
              }
            }}
          >
            <ModalHeader className="flex flex-col gap-1">
              <Trans>Ali ste prepričani, da želite izbrisati termin?</Trans>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col items-start gap-4">
                <LimeSelect
                  label={t`Razlog za izbris`}
                  placeholder={t`Izberite razlog za preklic`}
                  selectedKeys={[form.values.cancelReason]}
                  onSingleItemChange={(value) => {
                    form.setFieldValue(
                      "cancelReason",
                      value as AppointmentCancelReason,
                    );
                  }}
                  items={cancelReasonOptions.map((option) => ({
                    label: option.label,
                    key: option.value,
                  }))}
                />

                {form.values.cancelReason === "Custom" && (
                  <LimeInput
                    label={t`Vnesite razlog za odpoved`}
                    {...form.getInputProps("customCancelReason")}
                  />
                )}

                {isRepeating && (
                  <LimeCheckbox
                    data-identifier="delete-all-appointments-checkbox"
                    {...form.getInputProps("deleteAllRepeating", {
                      type: "checkbox",
                    })}
                  >
                    {t`Izbriši vse nadaljnje ponavljajoče termine?`}
                  </LimeCheckbox>
                )}

                {isLinked && (
                  <LimeCheckbox
                    {...form.getInputProps("deleteAllLinked", {
                      type: "checkbox",
                    })}
                  >
                    {t`Izbriši vse povezane termine?`}
                  </LimeCheckbox>
                )}

                {hasCancelNotification && (
                  <LimeCheckbox
                    {...form.getInputProps("sendCancelNotifications", {
                      type: "checkbox",
                    })}
                  >
                    {t`Pošlji obvestila?`}
                  </LimeCheckbox>
                )}

                <SubscriptionCancellationDecisions
                  options={cancelPreflight}
                  form={form}
                />

                <LimeAlert message={getPaymentErrorMessage} />

                <AppointmentRefundFields
                  payment={payment}
                  isLoadingPayment={isLoadingPayment}
                  userAppointmentId={userAppointmentId}
                  shouldRefundPaymentProps={form.getInputProps(
                    "shouldRefundPayment",
                    { type: "checkbox" },
                  )}
                  refundAmountProps={form.getInputProps("refundAmount")}
                  refundReasonProps={form.getInputProps("refundReason")}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="flex justify-end gap-2">
                <Button
                  variant="flat"
                  color="default"
                  onPress={onClose}
                  isDisabled={isSubmitting}
                >
                  <Trans>Prekliči</Trans>
                </Button>
                <Button
                  type="submit"
                  data-identifier="confirm-cancel-appointment-button"
                  color="primary"
                  isLoading={isSubmitting}
                >
                  <Trans>Izbriši</Trans>
                </Button>
              </div>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
};

export default AppointmentCancelModal;
